import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../stores/auth';

export function RequireAuth({
  children,
  allowMustChangePassword = false,
}: {
  children: ReactNode;
  /**
   * Allow rendering while `user.mustChangePassword` is true — only the
   * forced-password-change route sets this. Every other permission-gated
   * route 403s with `PASSWORD_CHANGE_REQUIRED` on its first data fetch
   * (`middleware/rbac.ts`'s `requirePermission`/`requireAnyPermission`) if a
   * user in this state reaches it, so the redirect has to happen here,
   * before any of that data fetching starts.
   */
  allowMustChangePassword?: boolean;
}) {
  const status = useAuth((s) => s.status);
  const mustChangePassword = useAuth((s) => s.user?.mustChangePassword ?? false);
  const location = useLocation();

  if (status !== 'authenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (mustChangePassword && !allowMustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }
  return <>{children}</>;
}

/**
 * Permission gate. Unauthorised UI is HIDDEN everywhere, not merely disabled —
 * a cashier should not see a "Payroll" tab they can never open.
 */
export function RequirePermission({
  permission,
  children,
  fallback,
}: {
  permission: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const hasPermission = useAuth((s) => s.hasPermission);

  if (!hasPermission(permission)) {
    return (
      <>
        {fallback ?? (
          <div className="card p-8 text-center">
            <p className="font-display text-lg">You don't have access to this</p>
            <p className="mt-1 text-sm text-ink2">
              Ask an administrator if you need it.
            </p>
          </div>
        )}
      </>
    );
  }
  return <>{children}</>;
}

/** Renders children only when the business's plan includes a module. */
export function RequireModule({ module, children }: { module: string; children: ReactNode }) {
  const user = useAuth((s) => s.user);
  if (!user?.modulesEnabled.includes(module)) return null;
  return <>{children}</>;
}

/** Hides write actions on a cloud mirror or a lapsed subscription. */
export function WhenWritable({ children }: { children: ReactNode }) {
  const canWrite = useAuth((s) => s.canWrite);
  if (!canWrite()) return null;
  return <>{children}</>;
}
